import { useEffect, useMemo, useState } from 'react'
import { Check, Search } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import { countryCodes, getCountryFromCode } from '@/shared/constants/countries'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'

export interface CountrySelectorProps {
  value: string
  onChange: (code: string) => void
  trigger?: React.ReactNode
}

export function CountrySelector({ value, onChange, trigger }: CountrySelectorProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selected = getCountryFromCode(value)

  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return countryCodes

    return countryCodes.filter(
      (country) =>
        country.name.toLowerCase().includes(normalized) ||
        country.code.toLowerCase().includes(normalized)
    )
  }, [query])

  const handleSelect = (code: string) => {
    onChange(code)
    setOpen(false)
  }

  const defaultTrigger = (
    <Button variant="outline" className="w-full justify-start">
      <span className="mr-2 text-lg">{selected.emoji}</span>
      <span className="truncate">{selected.name}</span>
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger ?? defaultTrigger}</DialogTrigger>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Select Country</DialogTitle>
          <DialogDescription>Search and select your country.</DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search country..."
              className="pl-9"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-1">
            {filtered.map((country) => {
              const isSelected = country.code === selected.code

              return (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleSelect(country.code)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg p-3 text-left transition-colors hover:bg-muted/50',
                    isSelected && 'bg-muted'
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="text-lg leading-none">{country.emoji}</span>
                    <span className="truncate font-medium">{country.name}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pl-3">
                    <span className="text-xs text-muted-foreground">{country.code}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </button>
              )
            })}

            {filtered.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No countries found.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
